import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from '@zauberware/react-i18n/web'
import { Space, Input, Button } from 'antd'

/**
 * Unified SearchControls component for TableControls.
 * Provides consistent search functionality with input field, search button, and optional reset button.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.value - Current search text value.
 * @param {Function} props.onChange - Callback when search text changes.
 * @param {Function} props.onSearch - Callback when search is triggered (button click or Enter).
 * @param {Function} [props.onReset] - Optional callback when reset button is clicked.
 * @param {string} [props.placeholder] - Placeholder text for search input.
 * @param {number} [props.width=200] - Width of the search input in pixels.
 * @param {string} [props.translationNamespace='common'] - Translation namespace for button labels.
 * @param {boolean} [props.showResetButton=true] - Whether to show the reset button.
 *
 * @returns {React.ReactElement} The rendered SearchControls component.
 *
 * @example
 * <SearchControls
 *   value={textSearch}
 *   onChange={setTextSearch}
 *   onSearch={handleSearch}
 *   onReset={handleReset}
 *   translationNamespace="orders"
 * />
 */
const SearchControls = ({
  value,
  onChange,
  onSearch,
  onReset,
  placeholder,
  width = 200,
  translationNamespace = 'common',
  showResetButton = true
}) => {
  const { t } = useTranslation([translationNamespace, 'common'])

  const handleInputChange = useCallback(
    e => {
      onChange(e.target.value)
    },
    [onChange]
  )

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        onSearch()
      }
    },
    [onSearch]
  )

  return (
    <Space>
      <Input
        placeholder={placeholder || t('common:search')}
        value={value}
        onChange={handleInputChange}
        onPressEnter={handleKeyPress}
        style={{ width }}
        aria-label={placeholder || t('common:search')}
      />
      <Button type="primary" onClick={onSearch} icon={<SearchOutlined />}>
        {t('common:search')}
      </Button>
      {showResetButton && onReset && <Button onClick={onReset}>{t('common:reset')}</Button>}
    </Space>
  )
}

SearchControls.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  placeholder: PropTypes.string,
  width: PropTypes.number,
  translationNamespace: PropTypes.string,
  showResetButton: PropTypes.bool
}

export default SearchControls
